import SmartLink from "@/components/ui/SmartLink";
import ContactChannels from "@/components/ContactChannels";
import { useSite } from "@/contexts/SiteContext";
import { activeChannels, filterChannelsByKeys } from "@/lib/contactChannels";
import { trackLead } from "@/lib/analyticsEvents";

/**
 * SectionCta — SINGURA implementare de buton CTA de contact/programare de pe
 * paginile active (vezi cta-spec.md secțiunile A/B). Nicio secțiune nu-și mai
 * scrie propria logică de buton — toate trec prin componenta asta.
 *
 * Citește chei PLATE din `content` (content-ul unei secțiuni SAU al unui item
 * dintr-un array de carduri), namespace-uite prin `prefix` ("cta" = primul/
 * singurul CTA al secțiunii, "cta2" = al doilea CTA, ex. hero principal+secundar):
 *
 *   `${prefix}_enabled`   bool     — implicit true (afișat); === false → nu randează nimic
 *   `${prefix}_mode`      string   — "channels" (implicit) | "link"
 *   `${prefix}_text`      string   — eticheta butonului (mode "link"); îndemnul de
 *                                    deasupra butoanelor (mode "channels", gol = fără îndemn)
 *   `${prefix}_link`      string   — destinația (mode "link"); fallback (mode "channels",
 *                                    când niciun canal configurat/activ nu rămâne după filtrare)
 *   `${prefix}_channels`  string[] — chei de canal (`site_settings.contact_channels[].key`)
 *                                    de arătat; gol/absent = toate canalele active
 *
 * Props:
 *   content       object  — obligatoriu; obiectul din care se citesc cheile de mai sus
 *                            (poate fi content-ul unei secțiuni sau al unui item dintr-un array)
 *   prefix        string  — implicit "cta"; "cta2" pentru al doilea CTA al unei secțiuni
 *   variant       string  — pass-through către <ContactChannels variant=.../> ("row" | "stack")
 *   className     string  — clase CSS aplicate elementului randat (wrapper-ul modului
 *                            "channels" cu buton clasic ca fallback)
 *   fieldPrefix   string  — implicit ""; prefix de dot-path pentru `data-field` (ca inline-editing-ul
 *                            din Live Editor să știe ce cheie din content editează), ex. "items.2."
 *                            pentru al 3-lea card dintr-un array (→ data-field="items.2.cta_text")
 *   fallbackText  string  — text folosit când content[`${prefix}_text`] lipsește/e gol
 *   fallbackLink  string  — link folosit când content[`${prefix}_link`] lipsește/e gol
 *
 * Comportament (identic cu cta-spec.md secțiunea B):
 *   - `cta_enabled === false` → nu randează nimic.
 *   - mode "channels" (implicit) → randează <ContactChannels only={cta_channels} .../>.
 *     Dacă, după filtrare, nu rămâne niciun canal activ → cade pe un buton clasic
 *     către `cta_link`/fallbackLink cu eticheta `cta_text`/fallbackText. Fără canale
 *     ȘI fără link → nu randează nimic.
 *   - mode "link" → buton clasic către `cta_link`/fallbackLink cu eticheta
 *     `cta_text`/fallbackText; fără link → nu randează nimic.
 *   - Folosește <SmartLink> pentru buton clasic (rute interne → <Link>, URL-uri
 *     externe precum wa.me/... puse direct în câmp → <a target="_blank">).
 *   - Emite `trackLead` la click în ambele moduri: în modul "channels" cu canale
 *     active, tracking-ul e deja emis de <ContactChannels/> (per-canal); pentru
 *     butonul clasic (mode "link" SAU fallback-ul din mode "channels"), SectionCta
 *     emite el însuși `trackLead(`${fieldPrefix}${prefix}`)`.
 *   - Pune `data-el={prefix}` pe elementul randat (pentru stilizarea per-element din
 *     Live Editor — vezi sectionElements.js) și `data-field` pe textul editabil.
 */
export default function SectionCta({
  content,
  prefix = "cta",
  variant = "row",
  className = "",
  fieldPrefix = "",
  fallbackText = "",
  fallbackLink = "",
}) {
  const { site } = useSite();
  const c = content || {};

  const kEnabled = `${prefix}_enabled`;
  const kMode = `${prefix}_mode`;
  const kText = `${prefix}_text`;
  const kLink = `${prefix}_link`;
  const kChannels = `${prefix}_channels`;

  if (c[kEnabled] === false) return null;

  const mode = c[kMode] === "link" ? "link" : "channels";
  const text = c[kText] || fallbackText || "";
  const link = c[kLink] || fallbackLink || "";
  const only = Array.isArray(c[kChannels]) ? c[kChannels] : [];

  const textField = `${fieldPrefix}${prefix}_text`;
  const trackSource = `${fieldPrefix}${prefix}`;

  const renderLinkButton = () => {
    if (!link) return null;
    return (
      <SmartLink
        to={link}
        data-el={prefix}
        data-testid={`section-cta-${prefix}-link`}
        className={className}
        onClick={() => trackLead(trackSource)}
      >
        <span data-field={textField}>{text}</span>
      </SmartLink>
    );
  };

  if (mode === "channels") {
    const channels = filterChannelsByKeys(activeChannels(site), only);
    if (channels.length > 0) {
      return (
        <div data-el={prefix} data-testid={`section-cta-${prefix}-channels`} className={className}>
          {text && <p data-field={textField}>{text}</p>}
          <ContactChannels only={only} variant={variant} />
        </div>
      );
    }
    return renderLinkButton();
  }

  return renderLinkButton();
}
